import { ref } from 'vue'
import { cloneDeep } from 'lodash-es'
import type { MultimediaProps } from '@canvas-builder/types/canvas'
import type { ComponentNode } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve } from '@openapi'
import type { CanvasThemeRetrieve } from '@lib/types/canvasTheme'
import useCanvasThemeAssets from '@canvas-builder/composables/useCanvasThemeAssets'

// Node state management
const nodeStateComponentNodesById = ref<Record<string, ComponentNode>>({})
const nodeStateUpdateNodeDataById = ref<((nodeId: string, data: MultimediaProps) => void) | null>(null)
const nodeStateIsInitialized = ref(false)

export default function useCanvasNodes() {
  function nodeStateInitialize(
    nodes: Record<string, ComponentNode>,
    updateFn: (nodeId: string, data: MultimediaProps) => void,
  ) {
    nodeStateComponentNodesById.value = nodes
    nodeStateUpdateNodeDataById.value = updateFn
    nodeStateIsInitialized.value = true
  }

  function nodeStateUpdate(componentNodesById: Record<string, ComponentNode>, activeCanvas: CanvasRetrieve | null) {
    if (nodeStateIsInitialized.value && nodeStateUpdateNodeDataById.value) {
      const allNodes: Record<string, ComponentNode> = {}

      // Add all canvas components
      Object.entries(componentNodesById).forEach(([key, value]) => {
        allNodes[key] = { ...value } as ComponentNode
      })

      // Add all sections components
      if (activeCanvas?.sections?.length) {
        activeCanvas.sections.forEach((section) => {
          if (section.content?.data) {
            const collectNodesFromSection = (nodes: ComponentNode[]) => {
              nodes.forEach((node) => {
                allNodes[node.id] = { ...node } as ComponentNode
                if (node.children?.length) {
                  collectNodesFromSection(node.children)
                }
              })
            }

            collectNodesFromSection(section.content.data)
          }
        })
      }

      nodeStateComponentNodesById.value = allNodes
    }
  }

  function updateMultimediaNodeWithThemeAsset(
    nodeId: string,
    multimediaProps: MultimediaProps,
    directNodeUpdate?: {
      node: ComponentNode
      section: CanvasRetrieve['sections'][number]
    },
    updateNodeDataById?: (id: string, data: MultimediaProps) => void,
    componentNodesById?: Record<string, ComponentNode>,
    activeCanvas?: CanvasRetrieve | null,
  ) {
    // Update multimedia node with theme asset
    // If a direct node reference is provided, update it directly
    if (directNodeUpdate && directNodeUpdate.node && directNodeUpdate.section) {
      // Found the node - update its data
      const propsWithoutThemeMeta = { ...multimediaProps }
      if ('theme_meta' in propsWithoutThemeMeta) {
        const { theme_meta, ...rest } = propsWithoutThemeMeta as any
        directNodeUpdate.node.data = rest
      } else {
        directNodeUpdate.node.data = propsWithoutThemeMeta
      }

      // Force the section's content to update by creating a new reference
      if (directNodeUpdate.section.content) {
        directNodeUpdate.section.content = {
          ...directNodeUpdate.section.content,
          data: [...directNodeUpdate.section.content.data],
        }
      }

      return true
    }

    if (!nodeStateIsInitialized.value) {
      return false
    }

    // First try to update using the node state
    if (nodeStateUpdateNodeDataById.value && nodeStateComponentNodesById.value[nodeId]) {
      // Make sure theme_meta is not in the data object
      const propsWithoutThemeMeta = { ...multimediaProps }
      if ('theme_meta' in propsWithoutThemeMeta) {
        const { theme_meta, ...rest } = propsWithoutThemeMeta as any
        nodeStateUpdateNodeDataById.value(nodeId, rest)
      } else {
        nodeStateUpdateNodeDataById.value(nodeId, propsWithoutThemeMeta)
      }
      return true
    }

    // If not found in node state, try to find and update in the active canvas main content
    if (updateNodeDataById && componentNodesById && componentNodesById[nodeId]) {
      // Make sure theme_meta is not in the data object
      const propsWithoutThemeMeta = { ...multimediaProps }
      if ('theme_meta' in propsWithoutThemeMeta) {
        const { theme_meta, ...rest } = propsWithoutThemeMeta as any
        updateNodeDataById(nodeId, rest)
      } else {
        updateNodeDataById(nodeId, propsWithoutThemeMeta)
      }
      return true
    }

    // If node not found in main canvas, check if it's inside a section
    if (activeCanvas?.sections?.length) {
      let foundInSection = false

      // Iterate through sections to find the node
      activeCanvas.sections.forEach((section) => {
        if (!section.content?.data?.length || foundInSection) return

        // Define a recursive function to find and update a node in the section content
        const findAndUpdateNodeInSection = (nodes: ComponentNode[]): boolean => {
          for (const node of nodes) {
            if (node.id === nodeId) {
              // Found the node - update its data
              const propsWithoutThemeMeta = { ...multimediaProps }
              if ('theme_meta' in propsWithoutThemeMeta) {
                const { theme_meta, ...rest } = propsWithoutThemeMeta as any
                node.data = rest
              } else {
                node.data = propsWithoutThemeMeta
              }

              // Force the section's content to update by creating a new reference
              section.content = { ...section.content, data: [...section.content.data] }

              foundInSection = true
              return true
            }

            // Check children recursively
            if (node.children?.length) {
              const found = findAndUpdateNodeInSection(node.children)
              if (found) return true
            }
          }

          return false
        }

        // Process the section's content
        findAndUpdateNodeInSection(section.content.data)
      })

      if (foundInSection) {
        return true
      }
    }

    return false
  }

  function updateComponentsWithThemeAssets(
    theme: CanvasThemeRetrieve,
    componentNodesById: Record<string, ComponentNode>,
    updateNodeDataById: (id: string, data: MultimediaProps) => void,
    activeCanvas: CanvasRetrieve | null,
  ) {
    if (!theme) return 0

    const { processThemeAssetsForNodes } = useCanvasThemeAssets()

    if (!nodeStateIsInitialized.value) {
      const nodes = {
        ...Object.fromEntries(Object.entries(componentNodesById).map(([key, value]) => [key, { ...value }])),
      } as Record<string, ComponentNode>

      nodeStateInitialize(nodes, updateNodeDataById)
    }

    // Always update node state with latest components before processing
    nodeStateUpdate(componentNodesById, activeCanvas)

    // Process all components from the canvas
    const canvasComponentsUpdated = processThemeAssetsForNodes(
      Object.values(componentNodesById) as ComponentNode[],
      theme,
      (nodeId, props, directNodeUpdate) =>
        updateMultimediaNodeWithThemeAsset(
          nodeId,
          props,
          directNodeUpdate,
          updateNodeDataById,
          componentNodesById,
          activeCanvas,
        ),
    )

    // Process sections separately if available
    let sectionComponentsUpdated = 0
    if (activeCanvas?.sections?.length) {
      activeCanvas.sections.forEach((section) => {
        if (section.content?.data?.length) {
          sectionComponentsUpdated += processThemeAssetsForNodes(
            section.content.data,
            theme,
            (nodeId, props, directNodeUpdate) =>
              updateMultimediaNodeWithThemeAsset(
                nodeId,
                props,
                directNodeUpdate,
                updateNodeDataById,
                componentNodesById,
                activeCanvas,
              ),
            section,
          )
        }
      })
    }

    return canvasComponentsUpdated + sectionComponentsUpdated
  }

  function nodeStateCleanup() {
    nodeStateComponentNodesById.value = {}
    nodeStateUpdateNodeDataById.value = null
    nodeStateIsInitialized.value = false
  }

  function processCanvasNodesForDisplay(nodes: ComponentNode[]): ComponentNode[] {
    return nodes.map((node) => {
      const processedNode = { ...node }

      if (processedNode.type === 'Multimedia') {
        if (processedNode.theme_meta) {
          if (!processedNode.data) {
            processedNode.data = {
              asset_type: 'pitcher_asset',
              file: { id: '', name: '', content_url: '' },
              type: 'image',
            } as MultimediaProps
          } else if (typeof processedNode.data === 'object') {
            const multimediaData = processedNode.data as any
            if (multimediaData.theme_meta) {
              const { theme_meta, ...dataWithoutThemeMeta } = multimediaData
              processedNode.data = dataWithoutThemeMeta
            }
          }
        } else if (processedNode.data && typeof processedNode.data === 'object') {
          const data = processedNode.data
          let hasEmptyContent = true

          if ('content_url' in data && data.content_url) {
            hasEmptyContent = false
          } else if ('external_url' in data && data.external_url) {
            hasEmptyContent = false
          } else if ('file' in data && data.file && typeof data.file === 'object') {
            if ('content_url' in data.file && data.file.content_url) {
              hasEmptyContent = false
            }
          }

          if (Object.keys(data).length > 0 && hasEmptyContent) {
            processedNode.data = { type: 'image' } as MultimediaProps
          }

          const multimediaData = processedNode.data as any
          if (multimediaData.theme_meta) {
            const { theme_meta, ...dataWithoutThemeMeta } = multimediaData
            processedNode.data = dataWithoutThemeMeta
          }
        }
      }

      if (processedNode.children && processedNode.children.length > 0) {
        processedNode.children = processCanvasNodesForDisplay(processedNode.children)
      }

      return processedNode
    })
  }

  function findNodeInSection(
    activeCanvas: CanvasRetrieve | null,
    sectionId: string,
    nodeId: string,
  ): ComponentNode | null {
    if (!activeCanvas?.sections) return null

    const section = activeCanvas.sections.find((s) => s.id === sectionId)
    if (!section || !section.content?.data?.length) return null

    let foundNode: ComponentNode | null = null

    const searchNodeInTree = (nodes: ComponentNode[]) => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          foundNode = node
          return true
        }

        if (node.children?.length) {
          const found = searchNodeInTree(node.children)
          if (found) return true
        }
      }

      return false
    }

    searchNodeInTree(section.content.data)

    return foundNode
  }

  function updateNodeInSection(
    activeCanvas: CanvasRetrieve | null,
    sectionId: string,
    nodeId: string,
    newData: ComponentNode['data'],
    setActiveCanvas: (canvas: CanvasRetrieve) => void,
  ): boolean {
    if (!activeCanvas?.sections) return false

    const newSections = cloneDeep(activeCanvas.sections)
    const section = newSections.find((s) => s.id === sectionId)

    if (!section || !section.content?.data?.length) return false

    let updateSuccessful = false

    const updateNodeInTree = (nodes: ComponentNode[]): boolean => {
      for (const node of nodes) {
        if (node.id === nodeId) {
          node.data = newData
          updateSuccessful = true
          return true
        }

        if (node.children?.length) {
          const updated = updateNodeInTree(node.children)
          if (updated) return true
        }
      }

      return false
    }

    updateNodeInTree(section.content.data)

    if (updateSuccessful) {
      const updatedCanvas = {
        ...activeCanvas,
        sections: newSections,
      }
      setActiveCanvas(updatedCanvas)
      return true
    }

    return false
  }

  return {
    nodeStateInitialize,
    nodeStateUpdate,
    nodeStateCleanup,
    updateMultimediaNodeWithThemeAsset,
    updateComponentsWithThemeAssets,
    processCanvasNodesForDisplay,
    findNodeInSection,
    updateNodeInSection,
  }
}
